import { useEffect } from 'react';

import { usePlanEditor } from './PlanEditorProvider';

export type KeyboardNavOptions = {
  /**
   * Off while a modal owns the keyboard. The listener already ignores typing in
   * a field, but once focus sits on a dialog's button ↑/↓/Enter would drive the
   * plan behind it.
   */
  enabled?: boolean;
};

/**
 * Tree-level keyboard navigation (active when no inline edit is in
 * progress): ↑/↓ move the active row, Enter toggles it, ⌘Enter adds a
 * task near it, Escape closes the Resources popover / clears the active row.
 */
export const useKeyboardNav = ({ enabled = true }: KeyboardNavOptions = {}) => {
  const { state, dispatch, flat, actions } = usePlanEditor();

  useEffect(() => {
    if (!enabled) return undefined;

    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target?.isContentEditable) {
        return;
      }
      if (state.editing) return;

      /*
        Alt+→ / Alt+← restructure the active row, the keyboard path that a
        pointer-only drag would otherwise leave missing. Placed before the
        vertical arrows, which are deliberately unmodified. preventDefault also
        suppresses Alt+← navigating Back on Windows browsers.
      */
      if (
        (event.key === 'ArrowRight' || event.key === 'ArrowLeft') &&
        event.altKey &&
        !event.metaKey &&
        !event.ctrlKey &&
        !event.shiftKey
      ) {
        const indent = event.key === 'ArrowRight';
        if (state.selectedIds.size > 0) {
          event.preventDefault();
          const orderedIds = flat.rows
            .filter((row) => state.selectedIds.has(row.id))
            .map((row) => row.id);
          dispatch({ type: indent ? 'BULK_INDENT' : 'BULK_OUTDENT', ids: orderedIds });
        } else if (state.activeId) {
          event.preventDefault();
          dispatch({ type: indent ? 'INDENT_NODE' : 'OUTDENT_NODE', id: state.activeId });
        }
        return;
      }

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const rows = flat.rows;
        if (rows.length === 0) return;
        const direction = event.key === 'ArrowDown' ? 1 : -1;
        const currentIndex = state.activeId
          ? (flat.index.rowIndexById.get(state.activeId) ?? -1)
          : -1;
        const nextIndex =
          currentIndex < 0
            ? direction > 0
              ? 0
              : rows.length - 1
            : Math.max(0, Math.min(rows.length - 1, currentIndex + direction));
        dispatch({ type: 'SET_ACTIVE', id: rows[nextIndex]!.id });
        return;
      }

      if (event.key === 'Enter') {
        const mod = event.metaKey || event.ctrlKey;
        if (mod) {
          event.preventDefault();
          actions.addTask(state.activeId);
          return;
        }
        return;
      }

      if (event.key === 'Escape') {
        if (state.resourcesNodeId) {
          dispatch({ type: 'CLOSE_RESOURCES' });
        } else if (state.activeId) {
          dispatch({ type: 'SET_ACTIVE', id: null });
        }
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [
    enabled,
    state.editing,
    state.activeId,
    state.resourcesNodeId,
    state.selectedIds,
    flat,
    dispatch,
    actions,
  ]);
};
